import React, { useState } from 'react';
import ConfirmModal from '../../../components/ui/ConfirmModal';
import StatusTag from '../../../components/ui/StatusTag';
import { ENVIRONMENTS, envMeta, envOf, productionBlockers } from '../../../utils/agentEnvironment';

/** Etiqueta del ambiente de un agente (no muestra nada si la base de datos aún no tiene el script 49). */
export function EnvironmentTag({ agent }) {
  const m = envMeta(envOf(agent));
  return m ? <StatusTag tone={m.tone} dot={m.key === 'production'}>{m.label}</StatusTag> : null;
}

/**
 * Cambiar el ambiente de un agente: demo, pruebas o producción. Pasar a producción pide confirmar y muestra lo que falta; salir de producción
 * también pide confirmar. Solo un admin usa producción (la base de datos lo exige): para un empleado esa opción va apagada.
 */
export default function EnvironmentPicker({ agent, credential, connections, project, canProduction, readOnly, onChange }) {
  const current = envOf(agent);
  const [ask, setAsk] = useState(null);   // ambiente al que se quiere pasar y falta confirmar
  if (!current) return null;
  const blockers = productionBlockers({ agent, credential, connections, project });
  const pick = (key) => {
    if (key === current) return;
    if (key === 'production' || current === 'production') setAsk(key); else onChange(key);
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span id="env-label" className="text-[13px] text-fg-dim">Ambiente</span>
      <div role="radiogroup" aria-labelledby="env-label" className="flex rounded-full bg-raised p-0.5 ring-1 ring-inset ring-line">
        {ENVIRONMENTS.map((e) => {
          const off = readOnly || (e.key === 'production' && !canProduction) || (current === 'production' && !canProduction);
          return (
            <button key={e.key} type="button" role="radio" aria-checked={current === e.key} disabled={off && current !== e.key} onClick={() => pick(e.key)} title={e.hint}
              className={`h-7 px-3 rounded-full text-[12.5px] font-semibold whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60 disabled:opacity-40 disabled:cursor-not-allowed
                ${current === e.key ? (e.key === 'production' ? 'bg-live text-ink' : 'bg-panel text-fg shadow-sm ring-1 ring-line') : 'text-fg-dim hover:text-fg'}`}>{e.label}</button>
          );
        })}
      </div>
      <ConfirmModal open={ask === 'production'} onClose={() => setAsk(null)} title={`Pasar «${agent.name}» a producción`} confirmLabel="Pasar a producción" confirmDisabled={blockers.length > 0}
        onConfirm={async () => { await onChange('production'); }}>
        {blockers.length ? (
          <>
            <p>Todavía no se puede. Falta:</p>
            <ul className="list-disc pl-5 space-y-1">{blockers.map((b) => <li key={b}>{b}</li>)}</ul>
          </>
        ) : (
          <>
            <p>Un agente en producción atiende a clientes reales. Mientras lo esté:</p>
            <ul className="list-disc pl-5 space-y-1"><li>no se puede archivar, despublicar ni borrar;</li><li>publicar cambios pide confirmación;</li><li>una persona del equipo que no sea administradora no puede editarlo.</li></ul>
          </>
        )}
      </ConfirmModal>
      <ConfirmModal open={Boolean(ask) && ask !== 'production'} onClose={() => setAsk(null)} title={`Sacar «${agent.name}» de producción`} confirmLabel={`Pasar a ${envMeta(ask)?.label?.toLowerCase() || ''}`}
        onConfirm={async () => { await onChange(ask); }}>
        <p>El agente sigue respondiendo mientras esté publicado, pero deja de marcarse como productivo: ya se podrá archivar o borrar y el equipo podrá editarlo con menos cuidado. Hazlo solo si ya no atiende a clientes reales.</p>
      </ConfirmModal>
    </div>
  );
}
